import { Fragment, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Reusable, web-based product-flow diagram, a horizontal sequence of steps with
 * connecting arrows, plus an optional benefit-chip row. Real DOM/SVG (no image),
 * responsive (vertical timeline on mobile, horizontal on desktop) and dark-mode
 * aware. One primitive powers the linear flows across the feature pages.
 *
 * Step node labels read as product/pipeline stage names and stay English (they
 * mirror the app UI); the section heading, subtitle and benefit chips localize.
 */

export type FlowAccent = 'info' | 'primary' | 'warning' | 'success' | 'danger' | 'cyan';

const ACCENTS: Record<FlowAccent, { icon: string; tile: string }> = {
  info: { icon: 'text-info-500', tile: 'bg-info-500/10 ring-info-500/30' },
  primary: { icon: 'text-primary-500', tile: 'bg-primary-500/10 ring-primary-500/30' },
  warning: { icon: 'text-warning-500', tile: 'bg-warning-500/10 ring-warning-500/30' },
  success: { icon: 'text-success-500', tile: 'bg-success-500/10 ring-success-500/30' },
  danger: { icon: 'text-red-500', tile: 'bg-red-500/10 ring-red-500/30' },
  cyan: { icon: 'text-cyan-500', tile: 'bg-cyan-500/10 ring-cyan-500/30' },
};

export interface FlowStep {
  icon: LucideIcon;
  /** English stage name (mirrors the app UI). */
  title: string;
  /** Short data/qualifier line under the title, e.g. "12,000 events". */
  sublabel?: string;
  accent: FlowAccent;
}

export interface FlowChip {
  icon: LucideIcon;
  title: string;
  sublabel?: string;
  accent?: FlowAccent;
}

export function ProductFlow({
  steps,
  chips,
  middle,
}: {
  steps: FlowStep[];
  chips?: FlowChip[];
  /** Optional bespoke visual rendered between the step row and the chips. */
  middle?: ReactNode;
}) {
  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:items-start">
        {steps.map((s, i) => {
          const a = ACCENTS[s.accent];
          const Icon = s.icon;
          return (
            <Fragment key={`${s.title}-${i}`}>
              <div className="flex lg:flex-col items-center lg:text-center gap-3.5 lg:gap-0 lg:flex-1">
                <div
                  className={cn(
                    'w-14 h-14 rounded-2xl flex items-center justify-center ring-1 shrink-0',
                    a.tile,
                  )}
                >
                  <Icon className={cn('h-6 w-6', a.icon)} />
                </div>
                <div className="lg:mt-3">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">
                    {s.title}
                  </p>
                  {s.sublabel && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{s.sublabel}</p>
                  )}
                </div>
              </div>
              {i < steps.length - 1 && (
                <div className="flex items-center justify-center shrink-0 text-gray-300 dark:text-gray-600 ml-[27px] lg:ml-0 lg:pt-5">
                  <ArrowRight className="h-4 w-4 rotate-90 lg:rotate-0 my-1 lg:my-0" />
                </div>
              )}
            </Fragment>
          );
        })}
      </div>

      {middle}

      {chips && chips.length > 0 && (
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {chips.map((c, i) => {
            const Icon = c.icon;
            const color = ACCENTS[c.accent ?? 'primary'].icon;
            return (
              <div
                key={`${c.title}-${i}`}
                className="flex items-start gap-2.5 rounded-xl glass-subtle p-3.5 border border-gray-200/60 dark:border-gray-800/60"
              >
                <Icon className={cn('h-4 w-4 mt-0.5 shrink-0', color)} />
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">
                    {c.title}
                  </p>
                  {c.sublabel && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{c.sublabel}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ProductFlowSection({
  title,
  subtitle,
  steps,
  chips,
  middle,
  gradient,
}: {
  title: string;
  subtitle?: string;
  steps: FlowStep[];
  chips?: FlowChip[];
  middle?: ReactNode;
  /** When true, paints the subtle section gradient background. */
  gradient?: boolean;
}) {
  return (
    <section className={cn('py-20 sm:py-24', gradient && 'gradient-subtle')}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center mb-14 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white text-balance">
            {title}
          </h2>
          {subtitle && (
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 text-balance">{subtitle}</p>
          )}
        </div>
        <ProductFlow steps={steps} chips={chips} middle={middle} />
      </div>
    </section>
  );
}
